import { Button, Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSaveAll } from "../hooks/saveAllProvider";
import { useConfirmExit } from "../hooks/useConfirmExit";

export const UNSAVED_PAGE_WARNING_DEFAULT_MESSAGE =
  "You have made changes. Do you want to save them before leaving?";
export const UNSAVED_PAGE_WARNING_DEFAULT_TITLE =
  "Are you sure you want to leave this page?";

const defaultApi = {
  message: UNSAVED_PAGE_WARNING_DEFAULT_MESSAGE,
  title: UNSAVED_PAGE_WARNING_DEFAULT_TITLE,
  disableSave: false,
  isUnsavedMemory: new Map(),
  addUnSavePageWarning: (options: {
    message?: string;
    title?: string;
    isUnsaved?: [key: string, value: boolean];
    onContinue?: () => void;
    disableSave?: boolean;
  }) => {},
  allowNavigation: () => {},
  isDictationActive: false,
};

export type UnsavedPageWarningContextValue = typeof defaultApi;

const UnsavedPageWarningContext =
  createContext<UnsavedPageWarningContextValue>(defaultApi);

/** True when rendered under a real UnsavedPageWarningProvider (not the default). */
const UnsavedPageWarningOwnershipContext = createContext(false);

// Export the context so it can be used with useContext directly
export { UnsavedPageWarningContext };

/**
 * This provider will warn the user if they try to leave the page with unsaved changes
 * * i.e not to be mistaken with the `useSaved` hook which should be used at an individual component level
 *
 * Nested providers are a no-op so only the outermost instance patches navigation.
 * That keeps recording + note-editing leave guards on the same page from overwriting
 * each other (e.g. AiNoteDetail wrapping Copilot).
 */
export const UnsavedPageWarningProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const hasOuterProvider = useContext(UnsavedPageWarningOwnershipContext);
  if (hasOuterProvider) {
    return <>{children}</>;
  }
  return (
    <UnsavedPageWarningProviderInner>
      {children}
    </UnsavedPageWarningProviderInner>
  );
};

const UnsavedPageWarningProviderInner = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isUnsavedMemory, setIsUnsavedMemory] = useState<Map<string, boolean>>(
    new Map([]),
  );
  const [message, setMessage] = useState(defaultApi.message);
  const [disableSave, setDisableSave] = useState(defaultApi.disableSave);
  const [title, setTitle] = useState(defaultApi.title);
  const [onContinue, setOnContinue] = useState<(() => void) | undefined>(
    undefined,
  );
  const { triggerSaveAll, isAllValid } = useSaveAll();
  const confirm = useConfirm();
  // Ref-based bypass so navigate() calls made right after a save (before
  // React re-renders and removes the guard) are not intercepted.
  const bypassGuardRef = useRef(false);

  const addUnSavePageWarning = useCallback(
    ({
      message,
      title: titleParam,
      isUnsaved,
      onContinue,
      disableSave,
    }: {
      message?: string;
      title?: string;
      isUnsaved?: [key: string, value: boolean];
      onContinue?: () => void;
      disableSave?: boolean;
    }) => {
      if (message) setMessage(message);
      if (onContinue) setOnContinue(() => onContinue);
      if (disableSave !== undefined) setDisableSave(disableSave);
      if (titleParam) setTitle(titleParam);

      if (!isUnsaved || isUnsaved.length !== 2) return;

      setIsUnsavedMemory((prev) => {
        // if memory already has same value, then return early without creating new Map
        const isInUnsavedMemory = prev.get(isUnsaved[0]);
        if (
          isInUnsavedMemory === isUnsaved[1] ||
          (isInUnsavedMemory === undefined && isUnsaved[1] === false)
        ) {
          return prev;
        }

        const newMap = new Map(prev);
        newMap.set(isUnsaved[0], isUnsaved[1]);
        return newMap;
      });
    },
    [],
  );

  const isUnsaved = useMemo(
    () => Array.from(isUnsavedMemory.values()).some((value) => value),
    [isUnsavedMemory],
  );

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!isUnsaved) return undefined;
      const confirmationMessage =
        "You have unsaved changes! Are you sure you want to leave?";
      event.returnValue = confirmationMessage; // For older browsers
      return confirmationMessage; // For modern browsers
    },
    [isUnsaved],
  );

  const handlePopState = useCallback(() => {
    confirm({
      title,
      content: (
        <>
          <p>{message}</p>
          {!isAllValid && (
            <Typography color="error">
              Form is invalid, add required fields to enable save and leave.
            </Typography>
          )}
          <Button
            style={{ display: disableSave ? "none" : "block" }}
            disabled={!isAllValid}
            onClick={() => {
              triggerSaveAll({
                afterSave: () => {
                  window.history.go(-1);
                },
              });
            }}
            sx={{
              marginRight: {
                sm: 1,
                xs: 0,
              },
              position: {
                sm: "absolute",
                xs: "relative",
              },
              left: {
                sm: 20,
                xs: -6,
              },
              bottom: {
                sm: 15,
                xs: 0,
              },
              width: {
                sm: "auto",
                xs: "calc(100% + 14px)",
              },
            }}
            variant="outlined"
            color="primary"
          >
            Save & Leave
          </Button>
        </>
      ),
      confirmationText: (
        <Button variant="contained" color="primary">
          Leave page
        </Button>
      ),
      cancellationText: (
        <Button variant="outlined" color="secondary">
          Stay on page
        </Button>
      ),
      dialogProps: {
        sx: {
          "& .MuiPaper-root": {
            border: "2px solid #000",
            color: "primary",
          },
        },
      },
      dialogActionsProps: {
        className: "unsaved-dialog",
        sx: {
          "& .MuiButton-root": {
            sm: {
              flex: "none",
            },
          },
          "&": {
            xs: {
              flexWrap: "wrap",
              ".MuiButton-root": {
                flex: "0 0 100%",
              },
            },
          },
        },
      },
    })
      .then(() => window.history.go(-1))
      .catch(() => {
        if (!window.history.state || !window.history.state.noBackExits) {
          window.history.pushState(
            { noBackExits: true },
            document.title,
            location.href,
          );
        }
      });
  }, [confirm, title, message, triggerSaveAll, isAllValid, disableSave]);

  useEffect(() => {
    if (isUnsaved) {
      window.addEventListener("beforeunload", handleBeforeUnload);
      if (!window.history.state || !window.history.state.noBackExits) {
        window.history.pushState(
          { noBackExits: true },
          document.title,
          location.href,
        );
      }
      window.addEventListener("popstate", handlePopState);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [handleBeforeUnload, handlePopState, isUnsaved]);

  /**
   * Call this synchronously before a programmatic navigate() that should bypass
   * the unsaved-changes guard (e.g. immediately after a successful save).
   */
  const allowNavigation = useCallback(() => {
    bypassGuardRef.current = true;
  }, []);

  const confirmExit = useCallback(
    (confirmNavigation: () => void) => {
      if (bypassGuardRef.current) {
        bypassGuardRef.current = false;
        confirmNavigation();
        return;
      }

      const onConfirm = () => {
        if (onContinue) onContinue();
        confirmNavigation();
      };

      confirm({
        title,
        content: (
          <>
            <p>{message}</p>
            {!isAllValid && (
              <Typography color="error">
                Form is invalid, add required fields to enable save and leave.
              </Typography>
            )}
            <Button
              style={{ display: disableSave ? "none" : "block" }}
              disabled={!isAllValid}
              onClick={() => {
                triggerSaveAll({
                  afterSave: onConfirm,
                });
              }}
              sx={{
                marginRight: {
                  sm: 1,
                  xs: 0,
                },
                position: {
                  sm: "absolute",
                  xs: "relative",
                },
                left: {
                  sm: 20,
                  xs: -6,
                },
                bottom: {
                  sm: 15,
                  xs: 0,
                },
                width: {
                  sm: "auto",
                  xs: "calc(100% + 14px)",
                },
              }}
              variant="outlined"
              color="primary"
            >
              Save & Leave
            </Button>
          </>
        ),
        confirmationText: (
          <Button variant="contained" color="primary">
            Leave page
          </Button>
        ),
        cancellationText: (
          <Button variant="outlined" color="secondary">
            Stay on page
          </Button>
        ),
        dialogProps: {
          sx: {
            "& .MuiPaper-root": {
              border: "2px solid #000",
              color: "primary",
            },
          },
        },
        dialogActionsProps: {
          className: "unsaved-dialog",
          sx: {
            "& .MuiButton-root": {
              sm: {
                flex: "none",
              },
            },
            "&": {
              xs: {
                flexWrap: "wrap",
                ".MuiButton-root": {
                  flex: "0 0 100%",
                },
              },
            },
          },
        },
      })
        .then(onConfirm)
        .catch(() => {});
    },
    [confirm, message, onContinue, triggerSaveAll, isAllValid, disableSave],
  );

  useConfirmExit(confirmExit, isUnsaved);

  const api = useMemo(() => {
    // Compute dictation state once per render instead of as a function
    // to prevent infinite loops in components that call this during render
    const isDictationActive = (() => {
      for (const [key, value] of isUnsavedMemory.entries()) {
        if (key.startsWith("dictation-") && value === true) {
          return true;
        }
      }
      return false;
    })();

    return {
      message,
      title,
      isUnsavedMemory,
      addUnSavePageWarning,
      allowNavigation,
      disableSave,
      isDictationActive,
    };
  }, [message, title, isUnsavedMemory, addUnSavePageWarning, disableSave]);

  return (
    <UnsavedPageWarningOwnershipContext.Provider value={true}>
      <UnsavedPageWarningContext.Provider value={api}>
        {children}
      </UnsavedPageWarningContext.Provider>
    </UnsavedPageWarningOwnershipContext.Provider>
  );
};

export const useUnsavedPageWarning = () => {
  const context = useContext(UnsavedPageWarningContext);
  if (!context) {
    throw new Error(
      "useUnsavedPageWarning must be used within an UnsavedPageWarningProvider",
    );
  }
  return context;
};
